/**
 * @author      xinzhe.chai@zumper.com (Xinzhe Chai)
 * @copyright   Copyright (c) 2021, Zumper
 */

import { useTheme } from '@emotion/react'

const FALLBACK_COLOR = '#726f6c'

export interface SpinnerProps {
  /**  Background color of the spinner */
  backgroundColor?: string
  /** Color of the Spinner */
  color?: string
  /** For testing purposes */
  dataTestId?: string
  /** In pixels (for height and width) */
  size?: number
}

/**
 * Renders a spinner with customizable color.
 */
export const Spinner = ({
  backgroundColor = 'none',
  color,
  dataTestId,
  size = 40,
}: SpinnerProps) => {
  const { colors } = useTheme()
  const colorValue = color ?? colors.text ?? FALLBACK_COLOR

  return (
    <svg
      data-testid={dataTestId}
      height={size}
      preserveAspectRatio="xMidYMid"
      style={{
        margin: 0,
        background: backgroundColor,
        display: 'block',
        shapeRendering: 'auto',
      }}
      viewBox="0 0 100 100"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
    >
      <g transform="rotate(0 50 50)">
        <rect fill={colorValue} height="12" rx="0" ry="0" width="3" x="48.5" y="24">
          <animate
            attributeName="opacity"
            begin="-0.935374149659864s"
            dur="1.0204081632653061s"
            keyTimes="0;1"
            repeatCount="indefinite"
            values="1;0"
          />
        </rect>
      </g>
      <g transform="rotate(30 50 50)">
        <rect fill={colorValue} height="12" rx="0" ry="0" width="3" x="48.5" y="24">
          <animate
            attributeName="opacity"
            begin="-0.8503401360544218s"
            dur="1.0204081632653061s"
            keyTimes="0;1"
            repeatCount="indefinite"
            values="1;0"
          />
        </rect>
      </g>
      <g transform="rotate(60 50 50)">
        <rect fill={colorValue} height="12" rx="0" ry="0" width="3" x="48.5" y="24">
          <animate
            attributeName="opacity"
            begin="-0.7653061224489796s"
            dur="1.0204081632653061s"
            keyTimes="0;1"
            repeatCount="indefinite"
            values="1;0"
          />
        </rect>
      </g>
      <g transform="rotate(90 50 50)">
        <rect fill={colorValue} height="12" rx="0" ry="0" width="3" x="48.5" y="24">
          <animate
            attributeName="opacity"
            begin="-0.6802721088435374s"
            dur="1.0204081632653061s"
            keyTimes="0;1"
            repeatCount="indefinite"
            values="1;0"
          />
        </rect>
      </g>
      <g transform="rotate(120 50 50)">
        <rect fill={colorValue} height="12" rx="0" ry="0" width="3" x="48.5" y="24">
          <animate
            attributeName="opacity"
            begin="-0.5952380952380952s"
            dur="1.0204081632653061s"
            keyTimes="0;1"
            repeatCount="indefinite"
            values="1;0"
          />
        </rect>
      </g>
      <g transform="rotate(150 50 50)">
        <rect fill={colorValue} height="12" rx="0" ry="0" width="3" x="48.5" y="24">
          <animate
            attributeName="opacity"
            begin="-0.5102040816326531s"
            dur="1.0204081632653061s"
            keyTimes="0;1"
            repeatCount="indefinite"
            values="1;0"
          />
        </rect>
      </g>
      <g transform="rotate(180 50 50)">
        <rect fill={colorValue} height="12" rx="0" ry="0" width="3" x="48.5" y="24">
          <animate
            attributeName="opacity"
            begin="-0.4251700680272109s"
            dur="1.0204081632653061s"
            keyTimes="0;1"
            repeatCount="indefinite"
            values="1;0"
          />
        </rect>
      </g>
      <g transform="rotate(210 50 50)">
        <rect fill={colorValue} height="12" rx="0" ry="0" width="3" x="48.5" y="24">
          <animate
            attributeName="opacity"
            begin="-0.3401360544217687s"
            dur="1.0204081632653061s"
            keyTimes="0;1"
            repeatCount="indefinite"
            values="1;0"
          />
        </rect>
      </g>
      <g transform="rotate(240 50 50)">
        <rect fill={colorValue} height="12" rx="0" ry="0" width="3" x="48.5" y="24">
          <animate
            attributeName="opacity"
            begin="-0.25510204081632654s"
            dur="1.0204081632653061s"
            keyTimes="0;1"
            repeatCount="indefinite"
            values="1;0"
          />
        </rect>
      </g>
      <g transform="rotate(270 50 50)">
        <rect fill={colorValue} height="12" rx="0" ry="0" width="3" x="48.5" y="24">
          <animate
            attributeName="opacity"
            begin="-0.17006802721088435s"
            dur="1.0204081632653061s"
            keyTimes="0;1"
            repeatCount="indefinite"
            values="1;0"
          />
        </rect>
      </g>
      <g transform="rotate(300 50 50)">
        <rect fill={colorValue} height="12" rx="0" ry="0" width="3" x="48.5" y="24">
          <animate
            attributeName="opacity"
            begin="-0.08503401360544217s"
            dur="1.0204081632653061s"
            keyTimes="0;1"
            repeatCount="indefinite"
            values="1;0"
          />
        </rect>
      </g>
      <g transform="rotate(330 50 50)">
        <rect fill={colorValue} height="12" rx="0" ry="0" width="3" x="48.5" y="24">
          <animate
            attributeName="opacity"
            begin="0s"
            dur="1.0204081632653061s"
            keyTimes="0;1"
            repeatCount="indefinite"
            values="1;0"
          />
        </rect>
      </g>
    </svg>
  )
}
